import React from 'react';
import { formatCategory } from '../../pages/inspections/categoryOptions';
import { FINDING_STATUS_LABEL } from './FindingFormModal';
import './FindingDetailModal.css';

const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

/**
 * Full read-only detail view for a single ComplianceFinding ("Risks &
 * Issues" tab's "View Details") - what was observed, its severity/RAG,
 * corrective action, and where it came from. Separate from
 * FindingFormModal (the edit form used by "Take Action") since looking at
 * an issue and editing it are different intents that shouldn't share one
 * always-editable form.
 *
 * Props:
 *  - finding: the full ComplianceFinding object, or null to stay hidden
 *  - onClose: () => void
 *  - onTakeAction: () => void - optional, switches to the edit form
 *  - onViewInspection: (inspectionId) => void - optional, opens the source
 *    inspection this finding was auto-raised from, if any
 */
const FindingDetailModal = ({ finding, onClose, onTakeAction, onViewInspection }) => {
  if (!finding) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="finding-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><i className="fas fa-exclamation-triangle"></i> Issue Details</h2>
          <button className="close-btn" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>

        <div className="modal-content">
          <h3 className="fdm-title">{finding.title}</h3>

          <div className="fdm-summary">
            <div className="fdm-summary-item">
              <span className="fdm-label">Category</span>
              <span className="fdm-value">{formatCategory(finding.category)}</span>
            </div>
            <div className="fdm-summary-item">
              <span className="fdm-label">Severity</span>
              <span className={`severity-badge ${finding.severity}`}>{finding.severity}</span>
            </div>
            <div className="fdm-summary-item">
              <span className="fdm-label">Status</span>
              <span className={`status-pill status-${finding.status}`}>{FINDING_STATUS_LABEL[finding.status] || finding.status}</span>
            </div>
            <div className="fdm-summary-item">
              <span className="fdm-label">RAG</span>
              <span className={`rag-badge rag-${finding.rag.toLowerCase()}`}>{finding.rag}</span>
            </div>
            <div className="fdm-summary-item">
              <span className="fdm-label">Date Raised</span>
              <span className="fdm-value">{formatDate(finding.dateRaised)}</span>
            </div>
            <div className="fdm-summary-item">
              <span className="fdm-label">Deadline</span>
              <span className="fdm-value">{formatDate(finding.deadline)}</span>
            </div>
            {finding.status === 'done' && (
              <div className="fdm-summary-item">
                <span className="fdm-label">Date Closed</span>
                <span className="fdm-value">{formatDate(finding.dateClosed)}</span>
              </div>
            )}
            {finding.assignee && (
              <div className="fdm-summary-item">
                <span className="fdm-label">Assigned To</span>
                <span className="fdm-value">{finding.assignee.firstName} {finding.assignee.lastName}</span>
              </div>
            )}
            <div className="fdm-summary-item">
              <span className="fdm-label">Source</span>
              <span className="fdm-value">
                {finding.source === 'inspection' ? 'Auto-raised from an inspection' : 'Logged manually'}
              </span>
            </div>
          </div>

          <h4 className="fdm-section-title">Description</h4>
          {finding.description ? (
            <p className="fdm-text-block">{finding.description}</p>
          ) : (
            <p className="fdm-empty">No description recorded.</p>
          )}

          <h4 className="fdm-section-title">Corrective Action</h4>
          {finding.correctiveAction ? (
            <p className="fdm-text-block">{finding.correctiveAction}</p>
          ) : (
            <p className="fdm-empty">No corrective action recorded yet.</p>
          )}

          {finding.source === 'inspection' && finding.inspectionId && onViewInspection && (
            <button
              type="button"
              className="btn-text fdm-view-inspection"
              onClick={() => onViewInspection(finding.inspectionId)}
            >
              <i className="fas fa-clipboard-check"></i> View source inspection
            </button>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
          {onTakeAction && (
            <button className="btn-primary-green" onClick={onTakeAction}>
              <i className="fas fa-edit"></i> Take Action
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FindingDetailModal;
